import React from 'react';
import loadable from '@loadable/component';

// State
import { exportableStore as store } from '../state/store';

// Components
import Home from '../scenes/Home';
import Order from '../scenes/Order';
import { Redirect } from 'react-router-dom';
import Allergens from '../scenes/Allergens';
import AllergensDescriptions from '../scenes/AllergensDescriptions';
import { CITY_THEME_DISNEY, isThemeActive } from '../utils/cityTheme';

export const paths = {
  home: '/',
  stockGifts: '/stock/gifts/:rewardIdx/:stockCode',
  stockConditions: '/stock/conditions/:conditionIdx/:stockCode',
  activateCode: '/activateCode',
  contacts: '/contacts',
  order: '/order',
  orderStep1: '/order/step1',
  orderStep2: '/order/step2',
  profile: '/profile',
  constructorAlias: '/constructor/:id',
  constructorAliasNotId: '/constructor',
  halves: '/halves',
  signin: '/signin',
  signup: '/signup',
  registerForm: '?registerForm=open',
  address: '/address',
  recoveryPassword: '/recovery-password',
  stock: '/stock',
  stockAlias: '/stock/:alias',
  orderTrackingAlias: '/order-tracking/:alias',
  comboboxAlias: '/combo-box/:alias',
  papabonus: '/papabonus',
  nutrition: '/nutrition',
  nutritionAlias: '/nutrition/:alias',
  chatMobile: '/chat',
  franchising: '/franczyza',
  franchisingSale: '/franczyza-for-sale',
  deletemyaccount: '/deletemyaccount',
  allergens: '/allergens',
  allergensDescriptions: '/allergens-descriptions',
  promo50Pizza: '/50pizza',
  toystory5: '/toystory5',
};
const fallback = (
  <div
    style={{
      height: '50%',
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'inherit',
      zIndex: 1,
    }}
  />
);

const AsyncPage = loadable(props => import(`../scenes/${props.page}`), {
  fallback: (
    <div
      style={{
        height: '50%',
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'inherit',
        zIndex: 1,
      }}
    />
  ),
});

const getDefaultRoutes = () => {
  const {
    city: { userCity },
    user: { isAuthenticated },
    responsive: { isPhone, isRealTablet, isRealPhone },
  } = store.getState();

  const sailplayType = userCity.sailplay?.type;
  const supertag = userCity?._supertag;
  const isDisneyTheme = isThemeActive(userCity, CITY_THEME_DISNEY);
  const getView = (phoneSupport = true, tabletSupport = true) => {
    if (phoneSupport && isRealPhone) {
      return 'MobileView';
    }

    if (tabletSupport && isRealTablet) {
      return 'TabletView';
    }

    return 'DesktopView';
  };
  const AsyncPage2 = loadable(props => import(`../scenes/${props.page}/${props.view}`), {
    fallback,
  });

  return {
    home: {
      path: '/',
      children: <Home />,
      layout: { simple: false, isScrollToTopDisabled: true },
    },
    stockGifts: {
      path: '/stock/gifts/:rewardIdx/:stockCode',
      children: <Home stockRole="rewards" />,
      layout: { simple: false, isScrollToTopDisabled: true },
    },
    stockConditions: {
      path: '/stock/conditions/:conditionIdx/:stockCode',
      children: <Home stockRole="conditions" />,
      layout: { simple: false, isScrollToTopDisabled: true },
    },
    // надо удалить, когда маркетологи перестанут использовать activateCode. попробуем в середине 2021
    activateCode: {
      path: '/activateCode',
      children: <Home />,
      redirect: true,
      layout: { simple: false, isScrollToTopDisabled: true },
    },
    contacts: {
      path: '/contacts',
      children: <AsyncPage page="Contacts" />,
      layout: { simple: false, theme: 'secondary' },
    },
    orderRedirect: {
      path: '/order',
      children: <Redirect to={`/${supertag}/order/step1`} />, // супертег добавится позже
      layout: { simple: true },
    },
    orderStep1: {
      path: '/order/step1',
      children: <Order step={1} />,
      layout: { simple: true },
      newest: true,
    },
    orderStep2: {
      path: '/order/step2',
      children: <Order step={2} />,
      layout: { simple: true },
      newest: true,
    },
    profile: {
      path: '/profile',
      children: <AsyncPage page="Profile" />,
      redirect: !isAuthenticated,
      layout: { simple: true },
    },
    constructorAlias: {
      path: '/constructor/:id',
      children: <AsyncPage page="Constructor" />,
      layout: { simple: true },
    },
    halves: {
      path: '/halves',
      children: <AsyncPage page="Halves" />,
      layout: { simple: true },
    },
    signin: {
      path: '/signin',
      children: <AsyncPage page="SignIn" />,
      // redirect: !isPhone,
      layout: { simple: true },
    },
    signup: {
      path: '/signup',
      children: <AsyncPage page="SignUp" />,
      // redirect: !isPhone,
      layout: { simple: true },
    },
    registerForm: {
      path: paths.registerForm,
      children: <AsyncPage page="SignUp" />,
      redirect: !isRealPhone || isAuthenticated,
      layout: { simple: true },
      newest: true,
    },
    recoveryPassword: {
      path: '/recovery-password',
      children: <AsyncPage page="RecoveryPassword" />,
      redirect: !isPhone,
      layout: { simple: true },
    },
    stock: {
      path: '/stock',
      children: <AsyncPage page="StockList" />,
      layout: { simple: true },
      newest: true,
    },
    stockAlias: {
      path: '/stock/:alias',
      children: <AsyncPage page="Stock" />,
      layout: { simple: true },
      newest: true,
    },
    orderTrackingAlias: {
      path: '/order-tracking/:alias',
      children: <AsyncPage page="OrderTracking" />,
      layout: { simple: true },
      newest: true,
    },
    comboboxAlias: {
      path: '/combo-box/:alias',
      children: <AsyncPage page="ComboBox" />,
      layout: { simple: true },
    },
    papabonus: {
      path: '/papabonus',
      children: <AsyncPage2 page="PapaBonus" view={getView(true, false)} />,
      redirect: sailplayType === 'none',
      layout: { simple: true, theme: 'secondary' },
      newest: true,
    },
    nutrition: {
      path: '/nutrition',
      children: <AsyncPage page="Nutrition" />,
      layout: { simple: false },
    },
    nutritionAlias: {
      path: '/nutrition/:alias',
      children: <AsyncPage page="Nutrition" />,
      layout: { simple: true },
    },
    franchising: {
      path: paths.franchising,
      children: <AsyncPage page="Franchising" />,
      layout: { simple: false },
    },
    franchisingSale: {
      path: paths.franchisingSale,
      children: <AsyncPage page="FranchisingForSale" />,
      layout: { simple: false },
    },
    deletemyaccount: {
      path: paths.deletemyaccount,
      children: <AsyncPage page="DeleteAccount" />,
      layout: { simple: false },
    },
    blog: {
      path: '/blog',
      children: <AsyncPage page="BlogList" />,
      layout: { simple: false },
      noSupertag: true,
    },
    blogAlias: {
      path: '/blog/:dynamicName',
      children: <AsyncPage page="Blog" />,
      layout: { simple: false },
      noSupertag: true,
    },
    allergens: {
      path: '/allergens',
      children: <Allergens />,
      layout: { simple: false },
      // noSupertag: true,
    },
    allergensDescription: {
      path: '/allergens-descriptions',
      children: <AllergensDescriptions />,
      layout: { simple: false },
      // noSupertag: true,
    },
    promo50Pizza: {
      path: paths.promo50Pizza,
      children: <AsyncPage page="Promo50Pizza" />,
      redirect: supertag?.toLowerCase() === 'pruszkow',
      layout: { simple: false },
    },
    toystory5: {
      path: paths.toystory5,
      children: <AsyncPage page="ToyStory5" />,
      redirect: !isDisneyTheme,
      layout: { simple: false },
    },
  };
};

const getSimpleRoutes = () => {
  const { pages = [], compositePages = [] } = store.getState().city.userCity;

  const defaultRoutes = getDefaultRoutes();
  const simpleRoutes = [...pages, ...compositePages]
    // убираем дубли
    .filter(
      (path, index, array) =>
        array.indexOf(path) === index &&
        !Object.values(defaultRoutes).some(route => route.path === path),
    )
    // добавляем к каждому простому пути компонент Page
    .map(path => ({
      path: `/${path}`,
      children: <AsyncPage page="Page" alias={path} />,
      layout: { simple: false, theme: 'secondary' },
    }));

  return Object.fromEntries(simpleRoutes.map(route => [route.path.slice(1), route]));
};

export const getAllRoutes = () => {
  const { _supertag } = store.getState().city.userCity;

  const defaultRoutes = getDefaultRoutes();
  const simpleRoutes = getSimpleRoutes();

  return [...Object.values(defaultRoutes), ...Object.values(simpleRoutes)].map(route => ({
    ...route,
    path: route.noSupertag
      ? route.path
      : route.path === '/'
      ? `/${_supertag}`
      : `/${_supertag}${route.path}`,
  }));
};

export const getRoute = (name, ...args) => {
  const { _supertag: supertag } = store.getState().city.userCity;

  const _supertag = supertag.toLowerCase();
  // если ничего не передали, то редиректим на главную
  if (!name) {
    return `/${_supertag}`;
  }

  const defaultRoutes = getDefaultRoutes();
  const simpleRoutes = getSimpleRoutes();
  const routes = { ...defaultRoutes, ...simpleRoutes };

  // если имя имеет ведущий слеш, то подрезаем его
  const newName = name[0] === '/' ? name.slice(1) : name;
  // ищем в наших страницах нужную (независимо от слеша и регистра)
  const currentKey = Object.keys(routes).find(key => key.toLowerCase() === newName.toLowerCase());

  // если нужной нет, то возвращаем адрес как есть, но с супертегом
  if (!currentKey) {
    // развилка для главной страницы, если пришел '/'
    return newName === '' ? `/${_supertag}` : `/${_supertag}/${newName}`;
  }

  if (!currentKey) {
    return newName === '' ? `/${_supertag}` : `/${_supertag}/${newName}`;
  }

  const currentRoute = routes[currentKey];
  const currentPath = currentRoute.path;

  // если нужно исключить супертег
  const addSupertag = !currentRoute.noSupertag;

  let i = 0;
  const newPath = currentPath
    .split('/')
    .map(part => (part[0] === ':' && args[i] !== undefined ? args[i++] : part))
    .join('/');

  return addSupertag ? `/${_supertag}${newPath}` : newPath;
};
